import { isSayWeeeApp } from '@/utils/weeeapp';
import Constants from '@/constants/const';
import { getCookie, getValueFromCookie, setCookie } from '@/utils/cookie';
import { isWeixinBrowser } from './common/device';
import { getbCookie } from '@/utils/common/user';
import { showGoogleOneTap } from './google-one-tap';
import { isLatinoChannel, isMainChannel } from './common/latino-app';

/**
 * 利用session token判断是否弹出first time use 弹窗
 * @param key
 */
export const updatePopupCreateAccountSession = () => {
  setCookie(Constants.POPUP_CREATE_ACCOUNT_SESSION, getCookie(Constants.WEEE_SESSION_TOKEN));
};

/**
 * 利用session token判断是否弹出first time use 弹窗
 * @param key
 */
export const shouldShowPopupCreateAccount = (query: any, ctx?) => {
  const ftu = query?.ftu;
  const ftuflags = query?.ftuflags;
  if (!ftu) return false;
  //随session token的过期策略走
  const oldSession = getCookie(Constants.POPUP_CREATE_ACCOUNT_SESSION, ctx);
  const newSession = getCookie(Constants.WEEE_SESSION_TOKEN, ctx);
  if (oldSession) {
    if (hasFtuFlags(ftuflags, Constants.FTU_FLAGS.ALWAYS_SHOW)) return true;
    return oldSession !== newSession;
  }
  if (ftu?.startsWith('popup')) return true;
  return false;
};

export const shouldShowAdsList = (ctx?) => {
  const shouldShowAds = getCookie(Constants.SHOULD_SHOW_ADS_LIST, ctx);
  return !!(shouldShowAds && shouldShowAds === getCookie(Constants.WEEE_SESSION_TOKEN, ctx));
};

export const hasFtuFlags = (ftuflags, targetFlag) => {
  ftuflags = parseInt(ftuflags);
  if (ftuflags === targetFlag) return true;
  return !!(ftuflags && (ftuflags & targetFlag) !== 0);
};

const UTM_SOURCE = 'Klaviyo';

const getLandingUrl = (ctx?) => {
  let url = getValueFromCookie(Constants.LANDING_URL, ctx) || '';
  if (!url) return '';
  return decodeURIComponent(url);
};

const hasUtmSource = (ctx?) => {
  const landingUrl = getLandingUrl(ctx);
  if (!!landingUrl && landingUrl.includes(`utm_source=${UTM_SOURCE}`)) {
    return true;
  }
  return false;
};

const ftuPayChannel = (ctx?) => {
  const landingUrl = getLandingUrl(ctx);
  if (!!landingUrl && landingUrl.includes(`ftu=popup`)) {
    return true;
  }
  return false;
};

export const couldShowFtuPopup = ({
  lang,
  isLogin,
  query,
  ctx,
  exp692
}: {
  lang: string;
  isLogin: boolean;
  query: any;
  ctx?: any;
  exp692: boolean;
}) => {
  // 用户utm_source=Klaviyo
  const utmSource = (!!query?.utm_source && query?.utm_source === UTM_SOURCE) || hasUtmSource(ctx);
  // 显示google一键登录
  const googleOneTap = showGoogleOneTap({ isLogin, query, ctx, exp692 });
  // // 如果第一次访问的是分享列表 也不弹ftu
  // const hideFtuForShareList = getCookie('hideFtuForShareList', ctx);
  // if (!!hideFtuForShareList) {
  //   return false;
  // }
  // app打开、微信浏览器打开、Klaviyo用户、google一键登录不展示ftu
  if (isSayWeeeApp(ctx) || isWeixinBrowser(ctx) || utmSource || googleOneTap) return false;
  return !!(
    (!isLogin &&
      shouldShowPopupCreateAccount(query, ctx) &&
      getCookie(Constants.EMAIL_COLLECT_POPUP_SHOWED, ctx) !== 'true') ||
    shouldShowAdsList(ctx)
  );
};

export const couldShowFtuPopupOrganic = ({
  lang,
  isLogin,
  query,
  ctx,
  exp692
}: {
  lang: string;
  isLogin: boolean;
  query: any;
  ctx?: any;
  exp692: boolean;
}) => {
  const ftuflags = query?.ftuflags ?? 48;
  // 付费渠道用户
  const ftuChannel = !!query.ftu || ftuPayChannel(ctx);
  // 显示google一键登录
  const googleOneTap = showGoogleOneTap({ isLogin, query, ctx, exp692 });
  // // 如果第一次访问的是分享列表 也不弹ftu
  // const hideFtuForShareList = getCookie('hideFtuForShareList', ctx);
  // if (!!hideFtuForShareList) {
  //   return false;
  // }
  // 用户utm_source=Klaviyo
  const utmSource = (!!query?.utm_source && query?.utm_source === UTM_SOURCE) || hasUtmSource(ctx);
  if (!!getFtuCookie(ctx)) return false;
  // 已登录、app打开、微信浏览器打开、付费渠道、Klaviyo用户、google一键登录不展示ftu
  if (isLogin || isSayWeeeApp(ctx) || isWeixinBrowser(ctx) || ftuChannel || utmSource || googleOneTap) return false;

  if (hasFtuFlags(ftuflags, Constants.FTU_FLAGS.ALWAYS_SHOW)) return true;

  //随session token的过期策略走
  const oldSession = getValueFromCookie(Constants.POPUP_CREATE_ACCOUNT_SESSION, ctx);
  const newSession = getValueFromCookie(Constants.WEEE_SESSION_TOKEN, ctx);
  if (oldSession === newSession || getCookie(Constants.EMAIL_COLLECT_POPUP_SHOWED, ctx) === 'true') {
    return false;
  }
  return true;
};

const isProd = (process.env.ENV_VAR || process.env.NEXT_PUBLIC_ENV_VAR) === 'prod';
export const ftuExperimentId = isProd ? 380 : 400;

export const getFtuCookie = (ctx?) => {
  return getValueFromCookie(Constants.FTU_COOKIE, ctx) || '';
};

export const setUserFtuCookie = (ctx?) => {
  if (!!getFtuCookie(ctx)) return;
  setCookie(Constants.FTU_COOKIE, getbCookie(ctx), ctx, { maxAge: Constants.FTU_COOKIE_EXPIRED_TIME });
};

export const doGoogleOneTapExperiment = async (query: Record<string, any>, ctx?: any) => {
  const userCanceledGoogleOneTap = getCookie(Constants.GSOPTOUT, ctx) === '1';
  if (userCanceledGoogleOneTap) return false;
  const googleOneTap = query?.googleOneTap === 'true';
  if (googleOneTap) return true;
  const latinoChannel = isLatinoChannel();
  if (!latinoChannel) return false;
  return false;
};

export const getOrganicTriggered = ({
  lang,
  isLogin,
  query,
  ctx,
  exp692
}: {
  lang: string;
  isLogin: boolean;
  query: any;
  ctx?: any;
  exp692: boolean;
}) => {
  let organicTriggered = false;
  if (couldShowFtuPopupOrganic({ lang, isLogin, query, ctx, exp692 })) {
    // 前两次页面切换弹popup，后面每3分钟最多弹一次
    const time = new Date().getTime() / 1000;
    const count = +(getValueFromCookie(Constants.FTU_SHOW_COUNT, ctx) || '0');
    const showTime = +(getValueFromCookie(Constants.FTU_SHOW_TIME, ctx) || '0');
    let interval = 10;
    if (count >= 2) {
      interval = 3 * 60;
    }
    const canShow = time - showTime - interval > 0;
    organicTriggered = canShow;
  }
  return organicTriggered;
};
